import React, {useMemo, useEffect} from 'react';
import {ScrollView, View} from 'react-native';
import {useStyle} from 'src/providers/style';
import {Input as DatePeriodInput} from 'src/hook-form-inputs/date-period-buttons';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {subtractNMonths} from 'src/common-utils/subtractNMonths';
import {compose} from 'recompose';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {
  AuthorizationProgress,
  NotesDue,
  SessionTotals,
  ClientStaffRetention,
  SupervisionCompliance,
  WeeklyServiceHours,
} from 'src/modules/practice-management/components/widgets';
import {Separator} from 'src/common-components/atoms';
import {useSessionDateSync} from 'src/hooks/useSessionDateSync';

const OperationalEfficiency = ({
  notesDue,
  activePatients,
  activeStaff,
  totalSessions,
  sessionsCompleted,
}: any) => {
  const styles = useStyle();
  const {syncDateRange} = useSessionDateSync();

  const methods = useForm({
    defaultValues: {
      startDate: subtractNMonths(1),
      endDate: new Date(),
    },
  });

  const watchedStartDate = useWatch({
    control: methods.control,
    name: 'startDate',
  });
  const watchedEndDate = useWatch({control: methods.control, name: 'endDate'});

  const memoizedSessionsCompleted = useMemo(
    () => sessionsCompleted,
    [sessionsCompleted],
  );

  // Sync sessions when date range changes
  useEffect(() => {
    if (watchedStartDate && watchedEndDate) {
      // Normalize dates - convert moment objects or strings to Date objects
      const normalizeDate = (date: any): Date => {
        if (date instanceof Date) {
          return date;
        }
        // Handle moment objects or ISO strings
        return new Date(date);
      };

      const startDate = normalizeDate(watchedStartDate);
      const endDate = normalizeDate(watchedEndDate);

      syncDateRange(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedStartDate, watchedEndDate]);

  return (
    <FormProvider {...methods}>
      <ScrollView>
        <View style={[styles.card, styles.paddingMTop]}>
          <View style={[styles.paddingHorizontal, styles.alignSelfStart]}>
            <DatePeriodInput name={'startDate'} />
          </View>
          <View
            style={[
              styles.container,
              styles.width,
              styles.paddingHorizontal,
              styles.paddingBottom,
            ]}>
            <View style={[styles.width, styles.flex, styles.paddingVertical]}>
              <SessionTotals
                totalSessions={totalSessions}
                sessionsCompleted={sessionsCompleted}
                notesDue={notesDue}
                startDate={watchedStartDate}
                endDate={watchedEndDate}
                from={'orgDashboard'}
              />
            </View>
            <Separator width={20} />
            <View style={[styles.width, styles.flex, styles.paddingVertical]}>
              <NotesDue
                notesDue={notesDue}
                startDate={watchedStartDate}
                endDate={watchedEndDate}
                from={'orgDashboard'}
              />
            </View>
          </View>
        </View>
        <View style={[styles.width, styles.flex]}>
          <View style={[styles.container, styles.width]}>
            <View style={[styles.width, styles.flex15, styles.height300]}>
              <AuthorizationProgress patients={activePatients} />
            </View>
            <View
              style={[
                styles.width,
                styles.flex,
                styles.container,
                styles.height200,
                styles.alignSelfCenter,
              ]}>
              <View
                style={[
                  styles.card,
                  styles.flex,
                  styles.alignCenter,
                  styles.justifyCenter,
                ]}>
                <ClientStaffRetention active={activePatients?.length || 0} />
              </View>

              <View
                style={[
                  styles.card,
                  styles.flex,
                  styles.alignCenter,
                  styles.justifyCenter,
                ]}>
                <ClientStaffRetention
                  active={activeStaff?.length || 0}
                  title={'Staff'}
                />
              </View>
            </View>
          </View>
          <View style={[styles.container, styles.width, styles.marginTop]}>
            <View style={[styles.width, styles.flex]}>
              <SupervisionCompliance sessions={memoizedSessionsCompleted} />
            </View>
          </View>
          <View style={[styles.container, styles.width, styles.marginTop]}>
            <View style={[styles.width, styles.flex]}>
              <WeeklyServiceHours sessions={memoizedSessionsCompleted} />
            </View>
          </View>
        </View>
      </ScrollView>
    </FormProvider>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['organization'], ({organization}: any) => ({
    notesDue: organization.notesDue(organization.partition),
    sessionsCompleted: organization.sessionsCompleted(organization.partition),
    totalSessions: organization.totalSessions(organization.partition),
    activePatients: organization.activePatients(organization.partition),
    activeStaff: organization.activeStaff(organization.partition),
  })),
)(OperationalEfficiency);
